import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  Button,
  VStack,
  Text,
  Input,
  Spinner,
  InputGroup,
  Flex,
  useToast,
  useBreakpointValue
} from "@chakra-ui/react";
import { InputNumber } from "antd";
import { useAuth } from "../context/Auth.context";
import { useNavigate } from "react-router-dom";
import * as api from "../api";
import { axios } from "../api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { forwardRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { QuantityInput } from "./QuantityInput";
import { ZoneRow } from "./ZoneRow";
import { AppointmentForm } from "./AppointmentForm";

const stripePromise = loadStripe(
  "pk_live_51NQWV6GCEd1WbZpak1nOX3uOa1VTs8ncT5WevhPy8Z1Atn6zkblHgXZLJqcLcdT7Z4BV1OV6GWwxi7l4QNl8ZQco004FuMFP5J"
);

const Appointment = () => {
  const { isAuthed, ready, user } = useAuth();
  const isAdminBooking = user?.roles?.includes("admin");
  const navigate = useNavigate();
  const [treatments, setTreatments] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [date, setDate] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [message, setMessage] = useState("");
  const [closures, setClosures] = useState([]);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [customInputValue, setCustomInputValue] = useState(null);
  const [optionError, setOptionError] = useState("");
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [zoneQuantities, setZoneQuantities] = useState({});
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (ready && !isAuthed) {
      setMessage("Je moet ingelogd zijn om een afspraak te boeken.");
      setTimeout(() => navigate("/login"), 2000);
    }
  }, [ready, isAuthed, navigate]);

  useEffect(() => {
    if (ready && isAuthed) {
      api.get(`/closures`)
        .then((res) => {
          const closureDates = res.items.flatMap(({ start_date, end_date }) => {
            const dates = [];
            let current = new Date(start_date);
            const end = new Date(end_date);
            while (current <= end) {
              dates.push(new Date(current));
              current.setDate(current.getDate() + 1);
            }
            return dates;
          });
          setClosures(closureDates);
        });

      api.get(`/appointments/fully-booked`)
        .then((res) => {
          const dates = res.map(date => new Date(date));
          setFullyBookedDates(dates);
        });
    }
  }, [ready, isAuthed]);

  useEffect(() => {
    if (ready && isAuthed) {
      api.get(`/treatments/visible`)
        .then((response) => {
          console.log("Treatments API response:", response);
          setTreatments(response.items || []);
        })
        .catch((error) => console.error("Error fetching treatments:", error));
    }
  }, [ready, isAuthed]);

  useEffect(() => {
    if (selectedTreatment) {
      api.get(`/treatments/${selectedTreatment}`).then((res) => {
        console.log("Treatment Options:", res.options);
        const mappedOptions = (res.options || []).map((opt) => ({
          ...opt,
          requiresInput: opt.requiresInput === 1,
        }));
        setTreatmentOptions(mappedOptions);
        setSelectedOptionId("");
        setCustomInputValue("");
        setZoneQuantities({});
      });
    } else {
      setTreatmentOptions([]);
      setCustomInputValue("");
      setZoneQuantities({});
    }
  }, [selectedTreatment]);

  useEffect(() => {
    if (treatmentOptions.length === 1) {
      setSelectedOptionId(treatmentOptions[0].id.toString());
    }
  }, [treatmentOptions]);


  const formatDateLocal = (date) => {
    const d = new Date(date);
    d.setHours(12, 0, 0, 0);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatDateForServer = (date) => {
    if (!date) return "";
    // Format als YYYY-MM-DD voor de server
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (ready && isAuthed && date) {
      setLoadingSlots(true);

      // Reset token voor deze aanroep
      const token = localStorage.getItem('jwtToken');
      if (token) {
        axios.defaults.headers['Authorization'] = `Bearer ${token}`; // Gebruik axios in plaats van api.default
      }

      const dateStr = formatDateForServer(date);
      console.log("Requesting time slots for date:", dateStr);

      api.get(`/appointments/available?date=${dateStr}`)
        .then((response) => {
          console.log("Time slots response:", response);
          // Hier verwerk je de response
          if (Array.isArray(response.availableSlots)) {
            setTimeSlots(response.availableSlots);
          } else if (typeof response.availableSlots === 'object' && response.availableSlots.message) {
            console.warn(response.availableSlots.message);
            setTimeSlots([]);
          } else {
            console.warn("Unexpected response format:", response);
            setTimeSlots([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching time slots:", error);
          setTimeSlots([]);
        })
        .finally(() => setLoadingSlots(false));
    }
  }, [date, ready, isAuthed]);

  const selectedOption = treatmentOptions.find(
    (o) => o.id.toString() === selectedOptionId
  );

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup mb={4} width="100%">
      <Input
        onClick={onClick}
        ref={ref}
        value={value}
        placeholder="Selecteer een datum"
        readOnly
        cursor="pointer"
        width="100%"
        height="40px"
        fontSize="md"
        marginLeft="0px"
      />
    </InputGroup>
  ));

  const isLaser =
    treatments.find(
      (t) => t.treatmentId.toString() === selectedTreatment
    )?.name === "Laserontharing";
  const isTattoo =
    treatments.find(
      (t) => t.treatmentId.toString() === selectedTreatment
    )?.name === "Verwijdering tatoeage";

  const totalPrice = React.useMemo(() => {
    // Voor Tattoo: prijs = lengte * 5
    if (isTattoo) {
      const length = parseFloat(customInputValue);
      return length > 0 ? length * 5 : 0;
    }
    // Voor Laser: per zone, als ingegeven aantal > 5, betaal je voor 5
    if (isLaser) {
      let sum = 0;
      for (const [optionId, quantity] of Object.entries(zoneQuantities)) {
        const opt = treatmentOptions.find((o) => o.id.toString() === optionId);
        if (!opt) continue;
        const q = parseInt(quantity, 10);
        if (!q || q <= 0) continue;
        const paidSessions = q > 5 ? 5 : q;
        sum += paidSessions * opt.price;
      }
      return sum;
    }
    // Als er wel treatmentOptions zijn maar er is nog geen optie geselecteerd, geef 0 terug
    if (treatmentOptions.length > 0 && !selectedOptionId) {
      return 0;
    }
    // Anders: als er treatmentOptions zijn en er is een optie geselecteerd, gebruik die prijs
    if (treatmentOptions.length > 0 && selectedOptionId) {
      const option = treatmentOptions.find((opt) => opt.id.toString() === selectedOptionId);
      return option ? Number(option.price) : 0;
    }
    // Fallback: gebruik de treatment prijs uit de database
    const selectedTreatmentObj = treatments.find(
      (t) => t.treatmentId.toString() === selectedTreatment.toString()
    );
    return selectedTreatmentObj ? Number(selectedTreatmentObj.price) : 0;
  }, [
    isTattoo,
    isLaser,
    zoneQuantities,
    treatmentOptions,
    selectedOptionId,
    customInputValue,
    selectedTreatment,
    treatments,
  ]);



  const handleBooking = async () => {
    if (!selectedTreatment || !date || !selectedTime) {
      setMessage("Gelieve alle velden in te vullen.");
      return;
    }

    const dateFormatted = formatDateLocal(date);
    const localDateTime = new Date(`${dateFormatted}T${selectedTime}`);
    const utcDateTime = new Date(
      localDateTime.getTime() - localDateTime.getTimezoneOffset() * 60000
    )
      .toISOString()
      .replace("T", " ")
      .slice(0, 19);
    const selectedTreatmentObj = treatments.find(
      (t) => t.treatmentId.toString() === selectedTreatment.toString()
    );
    const formattedDate = localDateTime.toLocaleDateString("nl-BE", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const formattedTime = localDateTime.toLocaleTimeString("nl-BE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    localStorage.setItem(
      "lastAppointmentInfo",
      JSON.stringify({
        treatmentName: selectedTreatmentObj?.name,
        formattedDate,
        formattedTime,
      })
    );

    let numericOptionIds = [];
    let zoneData = []; // Voor Laserontharing
    if (isLaser) {
      for (const [optionId, quantity] of Object.entries(zoneQuantities)) {
        const q = parseInt(quantity, 10);
        if (q > 0) {
          zoneData.push({ id: parseInt(optionId, 10), quantity: q });
        }
      }
    } else if (treatmentOptions.length > 0 && selectedOptionId) {
      numericOptionIds = [parseInt(selectedOptionId, 10)];
    }

    const requestData = {
      treatmentId: parseInt(selectedTreatment, 10),
      appointment_datetime: utcDateTime,
      userId: parseInt(localStorage.getItem("userId"), 10),
      inputValue: customInputValue ? parseInt(customInputValue, 10) : null,
      ...(isLaser ? { zoneData } : { optionIds: numericOptionIds }),
    };


    if (treatmentOptions.length > 0) {
      if (isLaser && Object.keys(zoneQuantities).length === 0) {
        setOptionError("Selecteer minstens één zone.");
        return;
      }
      if (!isLaser && !selectedOptionId) {
        setOptionError("Selecteer een optie.");
        return;
      }
    }

    const selectedOption = treatmentOptions.find(
      (o) => o.id.toString() === selectedOptionId
    );
    if (selectedOption?.requiresInput) {
      const value = parseInt(customInputValue);
      if (!value || value <= 0) {
        setOptionError("Voer een geldige lengte in (enkel hele getallen).");
        return;
      }
    }

    const adminOptions = [];
    if (isLaser) {
      for (const [id, qty] of Object.entries(zoneQuantities)) {
        const q = parseInt(qty, 10);
        if (q > 0) adminOptions.push({
          treatmentOptionId: parseInt(id, 10),
          quantity: q
        });
      }
    } else if (!isTattoo && selectedOptionId) {
      adminOptions.push({
        treatmentOptionId: parseInt(selectedOptionId, 10),
        quantity: 1
      });
    }

    setIsBooking(true);
    setMessage("");
    setOptionError("");

    if (isAdminBooking) {
      try {
        await api.post('/appointments/adminBooking', {
          treatmentId: parseInt(selectedTreatment, 10),
          appointment_datetime: utcDateTime,
          userId: parseInt(localStorage.getItem("userId"), 10),
          treatmentOptions: adminOptions,
          ...(isTattoo
            ? { length_cm: parseInt(customInputValue, 10) }
            : {})
        });
        setMessage("Afspraak succesvol ingepland!");
      } catch (err) {
        console.error("❌ Admin‑booking mislukt:", err);
        setMessage(
          err?.response?.data?.message ||
          "Kon afspraak niet inplannen."
        );
      } finally {
        setIsBooking(false);
      }
      return;
    }

    try {
      const stripe = await stripePromise;

      console.log("Sending request data:", requestData);
      const res = await api.post(`/stripe/create-checkout-session`, requestData);

      if (res && res.id) {
        await stripe.redirectToCheckout({ sessionId: res.id });
      } else {
        console.error("Stripe session ID missing in response:", res);
        toast({
          title: "Betalingsfout",
          description: "Kon de betalingssessie niet correct opstarten (geen ID ontvangen).",
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      }

    } catch (err) {
      console.error("❌ Error during create-checkout-session call:", err);
      let errorMessage = "Er is een onverwachte fout opgetreden.";
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast({
        title: "Boeking mislukt",
        description: errorMessage,
        status: "error",
        duration: 7000,
        isClosable: true,
      });

    } finally {
      setIsBooking(false);
    }
  };

  return (
    <>
      <Flex
        maxW="1200px"
        w="100%"
        mx="auto"
        justify="center"
        alignItems="center"
        align="flex-start"
        mt="80px"
        gap="50px"
        flexWrap="wrap"
      >
        {isTattoo && !isMobile && (
          <Box w="300px" display="flex" justifyContent="center">
            <img
              src="/images/tatoe4.webp"
              alt="Voorbeeld links"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
        )}


        <AppointmentForm
          isAuthed={isAuthed}
          isAdminBooking={isAdminBooking}
          message={message}
          handleBooking={handleBooking}
          treatments={treatments}
          selectedTreatment={selectedTreatment}
          setSelectedTreatment={setSelectedTreatment}
          treatmentOptions={treatmentOptions}
          selectedOptionId={selectedOptionId}
          setSelectedOptionId={setSelectedOptionId}
          customInputValue={customInputValue}
          setCustomInputValue={setCustomInputValue}
          optionError={optionError}
          totalPrice={totalPrice}
          isTattoo={isTattoo}
          isLaser={isLaser}
          zoneQuantities={zoneQuantities}
          setZoneQuantities={setZoneQuantities}
          closures={closures}
          fullyBookedDates={fullyBookedDates}
          date={date}
          setDate={setDate}
          timeSlots={timeSlots}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          loadingSlots={loadingSlots}
          formatDateLocal={formatDateLocal}
          isBooking={isBooking}
        />

        {isTattoo && isMobile && (
          <VStack spacing={4} mt={"-230px"} mb={"155px"} w="100%">
            <img
              src="/images/tatoe4.webp"
              alt="Voorbeeld links"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              }}
            />
            <img
              src="/images/tatoe5.webp"
              alt="Voorbeeld rechts"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              }}
            />
          </VStack>
        )}


        {isTattoo && !isMobile && (
          <Box w="300px" display="flex" justifyContent="center">
            <img
              src="/images/tatoe5.webp"
              alt="Voorbeeld rechts"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
        )}

      </Flex>

      {isTattoo && (
        <Box
          mt="-140px"
          mb="200px"
          maxW="800px"
          mx="auto"
          display="flex"
          justifyContent="center"
          gap={4}
          flexWrap="wrap"
        >
          {["tatoe1.webp", "tatoe2.webp", "tatoe3.webp"].map((img, index) => (
            <Box
              key={index}
              w={{ base: "100%", md: "30%" }}
              borderRadius="md"
              overflow="hidden"
            >
              <img
                src={`/images/${img}`}
                alt={`Tattoo meetvoorbeeld ${index + 1}`}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default Appointment;
